import { apiBase } from 'api/api';
import { apiErrorIfFailed } from 'api/utils/apiErrorMessage';
import { formatWithPattern, toDate } from 'util/dateFnsHelpers';
import { handleBlobReportResponse } from 'util/downloadReport';
import { cleanAddressField } from 'util/helper';
import { ApiResponse } from 'api/interface/api';
import {
    CreateFineRequest,
    CreateFineResponse,
    UpdateFineRequest,
    UpdateFineResponse,
    UpdateFinesRequest,
    UpdateFinesResponse,
    GetFineRequest,
    GetFineResponse,
    SaveCommentForFineRequest,
    SaveCommentForFineResponse,
    SaveCommentForMultipleFinesRequest,
    SaveCommentForMultipleFinesResponse,
    GetListOfFinesRequest,
    GetListOfFinesResponse,
    BackofficeGetFinesWithFilterParamsRequest,
    BackofficeGetFinesWithFilterParamsResponse,
    GetFineStatusesResponse,
    GetCarBrandsResponse,
    SetPaidFineMailRequest,
    Fine1,
    CancelFineRequest,
    CancelFineResponse,
    getFineTableDataRequest,
    getFineTableDataResponse,
    GenerateFineReportRequest,
    BackofficeGetFineCountResponse,
    GetListOfFinesCountRequest,
    OffenseResponse,
    OffenseDetailsResponse,
    CreateOffenseResponse,
    CancelReason,
    CancelReasonDetailsResponse,
    CreateCancelReasonResponse,
    GenerateFineUserReportRequest,
    PrintTemplateListResponse,
    PrintTemplateListItem,
    PrintTemplateDetailResponse,
    CreatePrintTemplateResponse,
    TemplateVariablesResponse,
    GetListOfFinesInterruptionReq,
    GetListOfFinesInterruptionResponse,
    GetListOfExternalNotesReq,
    GetListOfExternalNotesResponse,
    CreateExternalNoteResponse,
    ExternalNote,
} from 'api/interface/fine';
import { WrongDoingOption } from 'pages/loggedIn/backoffice/fines/interfaces';
import { Comment } from 'components/commentHistory/interface';
import { getCETTimestamp } from 'util/dateTimeConverter';
import { WrongDoing } from 'pages/loggedIn/backoffice/fines/wrongDoings/interfaces';

const createFine = async (
    req: CreateFineRequest,
    userId: number
): Promise<CreateFineResponse> => {
    const data = new FormData();
    data.append('user_id', String(userId));
    data.append('status', req.payload.status.toString());
    data.append('country_code', req.payload.country_code.toString());
    if (req.payload.wrong_doing_ids && req.payload.wrong_doing_ids.length > 0) {
        req.payload.wrong_doing_ids.forEach((id) =>
            data.append('wrong_doing_ids', id.toString())
        );
    } else {
        data.append('wrong_doing_id', req.payload.wrong_doing_id.toString());
    }
    data.append('note', req.payload.note.toString());
    data.append('internal_comment', req.payload.internal_comment.toString());
    data.append('reg_no', req.payload.reg_no.toString().trim());
    data.append('parkinglot_id', req.payload.parkinglot_id.toString());
    data.append('latitude', req.payload.latitude.toString());
    data.append('longitude', req.payload.longitude.toString());
    data.append('fine_origin_id', req.payload.fine_origin_id.toString());
    data.append('price', req.payload.price.toString());
    data.append('brand', (req.payload.brand ?? '').toString());
    data.append('model', (req.payload.model ?? '').toString());
    data.append('fuel', (req.payload.fuel ?? '').toString());

    // data.append('missing_price', req.payload.missingPrice.toString());
    if (req.payload.comment.length > 0) {
        for (let i = 0; i < req.payload.comment.length; i++) {
            data.append('comment', JSON.stringify(req.payload.comment[i]));
        }
    }
    if (req.payload.start_date) {
        data.append('start_date', toDate(req.payload.start_date).toISOString());
    }
    if (req.payload.end_date) {
        data.append('end_date', toDate(req.payload.end_date).toISOString());
    }
    if (req.payload.arrayFile.length > 0) {
        req.payload.arrayFile.forEach((file) => {
            data.append('file', file);
            data.append(
                'timestamp',
                formatWithPattern(file.lastModified, 'YYYY-MM-DD hh mm ss')
            );
        });
    }

    if (req.payload.missing_price !== undefined) {
        data.append('missing_price', req.payload.missing_price.toString());
    }
    if (req.payload.fee !== undefined) {
        data.append('cost', req.payload.fee.toString());
    }
    if (req.payload.duration_input !== undefined) {
        data.append('duration_input', req.payload.duration_input.toString());
    }

    const res = await apiBase.post('/v3/fines', data);

    const apiErr = apiErrorIfFailed(res);
    if (apiErr.error !== null) {
        return {
            error: apiErr.error,
            statusCode: apiErr.statusCode,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
    } as CreateFineResponse;
};

const updateFine = async (
    req: UpdateFineRequest
): Promise<UpdateFineResponse> => {
    const res = await apiBase.put(`/v3/fines/${req.payload.id}`, {
        ...req.payload,
    });

    const apiErr = apiErrorIfFailed(res);
    if (apiErr.error !== null) {
        return {
            error: apiErr.error,
            statusCode: apiErr.statusCode,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
    } as UpdateFineResponse;
};

const updateFines = async (
    payload: UpdateFinesRequest
): Promise<UpdateFinesResponse> => {
    const res = await apiBase.put(`/v3/fines/multi`, {
        ...payload,
    });

    const apiErr = apiErrorIfFailed(res);
    if (apiErr.error !== null) {
        return {
            error: apiErr.error,
            statusCode: apiErr.statusCode,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
    } as UpdateFinesResponse;
};

const getFine = async (req: GetFineRequest): Promise<GetFineResponse> => {
    const res = await apiBase.get(`/v3/fines/${req.id}`);

    const apiErr = apiErrorIfFailed(res);
    if (apiErr.error !== null) {
        return {
            error: apiErr.error,
            statusCode: apiErr.statusCode,
        };
    }

    res.data.created_at = getCETTimestamp(res.data.created_at);
    res.data.start_date = getCETTimestamp(res.data.start_date);
    res.data.end_date = getCETTimestamp(res.data.end_date);

    res.data.comment = (res.data.comment ?? []).map((comment: Comment) => ({
        ...comment,
        timestamp: new Date(getCETTimestamp(comment.timestamp.toString())),
    }));

    res.data.history = (res.data.history ?? []).map(
        (entry: { timestamp: string | Date }) => ({
            ...entry,
            timestamp: new Date(
                getCETTimestamp(
                    typeof entry.timestamp === 'string'
                        ? entry.timestamp
                        : entry.timestamp.toString()
                )
            ),
        })
    );

    const dmr = res.data.dmr_vehicle ?? null;

    res.data.brand = dmr?.brand_name ?? res.data.brand ?? '';
    res.data.model = dmr?.model_name ?? res.data.model ?? '';
    res.data.fuel = dmr?.fuel_name ?? res.data.fuel ?? '';
    res.data.vehicle_type = dmr?.vehicle_type ?? res.data.vehicle_type ?? '';

    if (res.data.parking_lot) {
        res.data.parking_lot.street_name = cleanAddressField(
            res.data.parking_lot.street_name
        );
        res.data.parking_lot.street_number = cleanAddressField(
            res.data.parking_lot.street_number
        );
        res.data.parking_lot.postal_code = cleanAddressField(
            res.data.parking_lot.postal_code
        );
        res.data.parking_lot.city = cleanAddressField(
            res.data.parking_lot.city
        );
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as GetFineResponse;
};

const saveCommentForFine = async (
    fineId: number,
    comment: SaveCommentForFineRequest[]
): Promise<SaveCommentForFineResponse> => {
    const res = await apiBase.post(`/v3/fines/${fineId}/comment`, {
        comment,
    });

    const apiErr = apiErrorIfFailed(res);
    if (apiErr.error !== null) {
        return {
            error: apiErr.error,
            statusCode: apiErr.statusCode,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
    } as SaveCommentForFineResponse;
};

const saveCommentForMultipleFines = async (
    Comments: SaveCommentForMultipleFinesRequest
): Promise<SaveCommentForMultipleFinesResponse> => {
    const res = await apiBase.post('/v3/fines/comment', {
        Comments,
    });

    const apiErr = apiErrorIfFailed(res);
    if (apiErr.error !== null) {
        return {
            error: apiErr.error,
            statusCode: apiErr.statusCode,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
    } as SaveCommentForMultipleFinesResponse;
};

const getListOfFines = async (
    req: GetListOfFinesRequest
): Promise<GetListOfFinesResponse> => {
    const res = await apiBase.get(`/v3/fines`, {
        params: req,
        paramsSerializer: (params) => {
            return new URLSearchParams(params).toString();
        },
    });

    const apiErr = apiErrorIfFailed(res);
    if (apiErr.error !== null) {
        return {
            error: apiErr.error,
            statusCode: apiErr.statusCode,
            data: [],
            total: 0,
        } as GetListOfFinesResponse;
    }

    return {
        error: null,
        statusCode: res.status,
        data: res.data.data.map((item: Fine1) => ({
            fine: {
                id: item.fine.id,
                qr_code: item.fine.qr_code,
                reg_no: item.fine.reg_no,
                car_brand: item.fine.car_brand,
                country_code: item.fine.country_code,
                created_at: getCETTimestamp(item.fine.created_at),
                status: item.fine.status,
                street_name: item.fine.street_name,
                street_name_qualifier: item.fine.street_name_qualifier,
                street_number: item.fine.street_number,
            },
            address: {
                id: item.address.id,
                address_name: item.address.address_name,
                street_name: cleanAddressField(item.address.street_name),
                street_number: cleanAddressField(item.address.street_number),
                zipcode: cleanAddressField(item.address.zipcode),
                city: cleanAddressField(item.address.city),
            },
            user: {
                name: item.user.name,
                username: item.user.username,
                user_note: item.user.user_note,
            },
            organisation: {
                name: item.organisation.name,
            },
        })),
        total: res.data.total, // Assuming `total` is returned as a root-level field in the response
    } as GetListOfFinesResponse;
};

const FINE_REPORT_ROW_LIMIT_502 =
    'Unable to generate report. The amount of permits were greater than 50000 - please apply filter to minimize the amount.';

const generateFineReport = async (
    req: GenerateFineReportRequest
): Promise<ApiResponse> => {
    const res = await apiBase.get(`/v3/fines/generate-report`, {
        params: req,
        paramsSerializer: (params) => {
            return new URLSearchParams(params).toString();
        },
        responseType: 'blob',
        timeout: 0,
    });

    const result = await handleBlobReportResponse(res, {
        limit502Message: FINE_REPORT_ROW_LIMIT_502,
        non200Error: 'bad request',
    });

    return {
        error: result.error,
        statusCode: result.statusCode,
    } as ApiResponse;
};

const getFinesWithFilterParams = async (
    req: BackofficeGetFinesWithFilterParamsRequest
): Promise<BackofficeGetFinesWithFilterParamsResponse> => {
    const res = await apiBase.get(`/v3/fines/stats`, {
        params: req,
        paramsSerializer: (params) => {
            return new URLSearchParams(params).toString();
        },
    });

    const apiErr = apiErrorIfFailed(res);
    if (apiErr.error !== null) {
        return {
            payload: [],
            error: apiErr.error,
            statusCode: apiErr.statusCode,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as BackofficeGetFinesWithFilterParamsResponse;
};

const getFineStatusOptions = async (): Promise<GetFineStatusesResponse> => {
    const res = await apiBase.get(`/v3/fines/status`);
    if (res?.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: [],
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as GetFineStatusesResponse;
};

const getCarBrands = async (): Promise<GetCarBrandsResponse> => {
    const res = await apiBase.get(`/v3/fines/car-brands`);
    if (!res || res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res?.status ?? 500,
            payload: [],
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as GetCarBrandsResponse;
};

const sendPaidFineMail = async (
    req: SetPaidFineMailRequest
): Promise<ApiResponse> => {
    const res = await apiBase.post('/v3/fines/send-fine-receipt', req);

    const apiErr = apiErrorIfFailed(res);
    if (apiErr.error !== null) {
        return {
            error: apiErr.error,
            statusCode: apiErr.statusCode,
        };
    }
    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const cancelFine = async (
    req: CancelFineRequest
): Promise<CancelFineResponse> => {
    const res = await apiBase.get(`/v3/fines/cancel/${req.fine_id}`, {
        params: {
            comment: req.comment,
            cancel_reason_id: req.cancel_reason_id,
        },
    });

    const apiErr = apiErrorIfFailed(res);
    if (apiErr.error !== null) {
        return {
            error: apiErr.error,
            statusCode: apiErr.statusCode,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data.payload,
    } as CancelFineResponse;
};

const getFineTableData = async (
    req: getFineTableDataRequest
): Promise<getFineTableDataResponse> => {
    const res = await apiBase.get('/v3/fines', {
        params: req,
        paramsSerializer: (p) => {
            return new URLSearchParams(p).toString();
        },
    });

    const apiErr = apiErrorIfFailed(res);
    if (apiErr.error !== null) {
        return {
            payload: [],
            total: 0,
            error: apiErr.error,
            statusCode: apiErr.statusCode,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data.data,
        total: res.data.total,
    } as getFineTableDataResponse;
};

const getFinesCount = async (
    req: GetListOfFinesCountRequest
): Promise<BackofficeGetFineCountResponse> => {
    const res = await apiBase.get(`/v3/fines/count`, {
        params: req,
        paramsSerializer: (params) => {
            return new URLSearchParams(params).toString();
        },
    });

    const apiErr = apiErrorIfFailed(res);
    if (apiErr.error !== null) {
        return {
            error: apiErr.error,
            statusCode: apiErr.statusCode,
            total: 0,
        };
    }

    return {
        error: null,
        statusCode: res.status,
        total: res.data.total,
    };
};

const getOffense = async (parkinglotId?: number): Promise<OffenseResponse> => {
    const params = parkinglotId ? `?parkinglot_ids=${parkinglotId}` : '';
    const res = await apiBase.get(`/v3/fines/offense${params}`);
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: [],
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as OffenseResponse;
};

const getOffenseDetails = async ({
    limit,
    offset,
    searchTerm,
    sortColumn,
    sortOrder,
}: {
    limit: number;
    offset: number;
    searchTerm: string;
    sortColumn?: string;
    sortOrder?: string;
}): Promise<OffenseDetailsResponse> => {
    const res = await apiBase.get(
        `/v3/fines/offense/details?limit=${limit}&offset=${offset}&search=${searchTerm}&sort_column=${sortColumn}&sort_order=${sortOrder}`
    );

    if (res.status !== 200) {
        return {
            error: `${res.data.error || ''}: ${res.data?.details || ''}`,
            statusCode: res.status,
            payload: {
                data: [] as WrongDoing[],
                count: 0,
                available_actions: [],
            },
        };
    }

    const payload = {
        data: res.data.data.map(
            (item: WrongDoing) =>
                ({
                    ...item,
                    key_values:
                        typeof item.key_values === 'string'
                            ? JSON.parse(item.key_values)
                            : item.key_values,
                    custom_observation_time:
                        item.custom_observation_time != null // Converts the seconds into minutes to show since we are saving in seconds in DB.
                            ? item.custom_observation_time / 60
                            : null,
                }) as WrongDoing
        ),
        count: res.data.count,
        available_actions: res.data.available_actions ?? [],
    };

    return {
        error: null,
        statusCode: res.status,
        payload,
    } as OffenseDetailsResponse;
};

const getFineClearReasons = async ({
    limit,
    offset,
    searchTerm,
    sortColumn,
    sortOrder,
}: {
    limit: number;
    offset: number;
    searchTerm: string;
    sortColumn?: string;
    sortOrder?: string;
}): Promise<OffenseDetailsResponse> => {
    const res = await apiBase.get(
        `/v3/fines/clear-reasons/details?limit=${limit}&offset=${offset}&search=${searchTerm}&sort_column=${sortColumn}&sort_order=${sortOrder}`
    );

    if (res.status !== 200) {
        return {
            error: `${res.data.error || ''}: ${res.data?.details || ''}`,
            statusCode: res.status,
            payload: {
                data: [] as WrongDoing[],
                count: 0,
                available_actions: [],
            },
        };
    }

    const payload = {
        data: res.data.data.map(
            (item: WrongDoing) =>
                ({
                    ...item,
                    key_values:
                        typeof item.key_values === 'string'
                            ? JSON.parse(item.key_values)
                            : item.key_values,
                }) as WrongDoing
        ),
        count: res.data.count,
        available_actions: res.data.available_actions ?? [],
    };

    return {
        error: null,
        statusCode: res.status,
        payload,
    } as OffenseDetailsResponse;
};
const createFineClearDetails = async (
    wdData: WrongDoing
): Promise<ApiResponse> => {
    const res = await apiBase.post(`/v3/fines/clear-reasons/details`, {
        name: wdData.name,
        debbie_name: wdData.debbie_name,
        key_values: JSON.stringify(wdData.key_values),
    });
    if (res.status >= 400) {
        return {
            error: `${res.data.error || ''}: ${res.data?.details || ''}`,
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as CreateOffenseResponse;
};
const updateClearReasonsDetails = async (
    wdData: WrongDoing
): Promise<ApiResponse> => {
    const res = await apiBase.put(
        `/v3/fines/clear-reasons/details/${wdData.id}`,
        {
            name: wdData.name,
            debbie_name: wdData.debbie_name,
            key_values: JSON.stringify(wdData.key_values),
        }
    );
    if (res.status >= 400) {
        return {
            error: `${res.data.error || ''}: ${res.data?.details || ''}`,
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const updateOffenseDetails = async (
    wdData: WrongDoing
): Promise<ApiResponse> => {
    const res = await apiBase.put(`/v3/fines/offense/details/${wdData.id}`, {
        name: wdData.name,
        debbie_name: wdData.debbie_name,
        key_values: JSON.stringify(wdData.key_values),
        actions: wdData.actions ?? [],
        custom_observation_time:
            wdData.custom_observation_time != null // Converts the minutes into second to store in DB. In DB it needs to store in seconds.
                ? wdData.custom_observation_time * 60
                : null,
        custom_fine_price: wdData.custom_fine_price ?? null,
        code: wdData.code ?? '',
    });

    if (res.status >= 400) {
        return {
            error: 'Failed to update offense details',
            statusCode: res.status,
        };
    }

    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const createOffenseDetails = async (
    wdData: WrongDoing
): Promise<ApiResponse> => {
    const res = await apiBase.post(`/v3/fines/offense/details`, {
        name: wdData.name,
        debbie_name: wdData.debbie_name,
        key_values: JSON.stringify(wdData.key_values),
        actions: wdData.actions.map((action) => ({
            id: action.id,
            type: action.type,
            label: action.label,
        })),
        custom_observation_time:
            wdData.custom_observation_time != null // Converts the minutes into second to store in DB. In DB it needs to store in seconds.
                ? wdData.custom_observation_time * 60
                : null,
        custom_fine_price: wdData.custom_fine_price ?? null,
        code: wdData.code ?? '',
    });
    if (res.status >= 400) {
        return {
            error: `${res.data.error || ''}: ${res.data?.details || ''}`,
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as CreateOffenseResponse;
};

const deleteOffenseDetails = async (id: number): Promise<ApiResponse> => {
    const res = await apiBase.delete(`/v3/fines/offense/details/${id}`);

    if (res.status >= 400) {
        return {
            error: `${res.data.error || ''}: ${res.data?.details || ''}`,
            statusCode: res.status,
        };
    }

    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};
const deleteClearDetails = async (id: number): Promise<ApiResponse> => {
    const res = await apiBase.delete(`/v3/fines/clear-reasons/details/${id}`);

    if (res.status >= 400) {
        return {
            error: `${res.data.error || ''}: ${res.data?.details || ''}`,
            statusCode: res.status,
        };
    }

    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const getAvailableWrongDoings = async (params: { address_id: number }) => {
    const res = await apiBase.get(
        `/v3/fines/available-wrong-doings?address_id=${params.address_id}`
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: [] as WrongDoingOption[],
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data as WrongDoingOption[],
    };
};

// Cancel Reason Details API Functions
const getCancelReasonDetails = async (params: {
    limit: number;
    offset: number;
    searchTerm: string;
    sortColumn?: string;
    sortOrder?: string;
    statusId?: number;
}): Promise<CancelReasonDetailsResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('limit', params.limit.toString());
    queryParams.append('offset', params.offset.toString());
    queryParams.append('search_term', params.searchTerm);

    if (params.sortColumn) {
        queryParams.append('sort_column', params.sortColumn);
    }
    if (params.sortOrder) {
        queryParams.append('sort_order', params.sortOrder);
    }
    if (params.statusId != null) {
        queryParams.append('status_id', params.statusId.toString());
    }

    const res = await apiBase.get(
        `/v3/fines/cancel-reasons/details?${queryParams.toString()}`
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                data: [] as CancelReason[],
                count: 0,
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            ...res.data,
            data: (res.data.data || []).map((item: CancelReason) => ({
                ...item,
                key_values:
                    typeof item.key_values === 'string' && item.key_values
                        ? JSON.parse(item.key_values)
                        : (item.key_values ?? {}),
            })),
        },
    } as CancelReasonDetailsResponse;
};

const createCancelReasonDetails = async (
    cancelReasonData: CancelReason
): Promise<CreateCancelReasonResponse> => {
    const res = await apiBase.post(`/v3/fines/cancel-reasons/details`, {
        name: cancelReasonData.name,
        description: cancelReasonData.description,
        key_values: JSON.stringify(cancelReasonData.key_values),
    });

    if (res.status > 299) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                id: 0,
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as CreateCancelReasonResponse;
};

const updateCancelReasonDetails = async (
    cancelReasonData: CancelReason
): Promise<ApiResponse> => {
    const res = await apiBase.put(
        `/v3/fines/cancel-reasons/details/${cancelReasonData.id}`,
        {
            id: cancelReasonData.id,
            name: cancelReasonData.name,
            description: cancelReasonData.description,
            key_values: JSON.stringify(cancelReasonData.key_values),
        }
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        } as ApiResponse;
    }

    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const deleteCancelReasonDetails = async (id: number): Promise<ApiResponse> => {
    const res = await apiBase.delete(`/v3/fines/cancel-reasons/details/${id}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        } as ApiResponse;
    }
    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const generateEventLogReport = async (
    req: GenerateFineUserReportRequest
): Promise<ApiResponse> => {
    const res = await apiBase.get(`/v3/parkinglots/eventlog/report`, {
        params: req,
        paramsSerializer: (params) => {
            const filtered = Object.fromEntries(
                Object.entries(params).filter(
                    ([, v]) => v !== undefined && v !== '' && v !== null
                )
            );
            return new URLSearchParams(filtered).toString();
        },
        responseType: 'blob',
        timeout: 0,
    });

    const result = await handleBlobReportResponse(res, {
        limit502Message: FINE_REPORT_ROW_LIMIT_502,
        non200Error: 'bad request',
    });

    return {
        error: result.error,
        statusCode: result.statusCode,
    } as ApiResponse;
};

// Print Template API Functions
const getPrintTemplates = async (): Promise<PrintTemplateListResponse> => {
    const res = await apiBase.get(`/v3/fines/print-templates`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                templates: [] as PrintTemplateListItem[],
                meta: {
                    total_templates: 0,
                    active_count: 0,
                    inactive_count: 0,
                },
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as PrintTemplateListResponse;
};

const getPrintTemplateDetail = async (
    id: number
): Promise<PrintTemplateDetailResponse> => {
    const res = await apiBase.get(`/v3/fines/print-templates/${id}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                id: 0,
                template_name: '',
                logo_url: null,
                company_name: null,
                legal_entity: null,
                cvr_number: null,
                fik_number: null,
                qr_code_url: null,
                website_url: null,
                active: false,
                blocks: [],
                created_at: '',
                updated_at: '',
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as PrintTemplateDetailResponse;
};

const createPrintTemplate = async (data: {
    template_name: string;
    logo_url: string | null;
    company_name: string | null;
    legal_entity: string | null;
    cvr_number: string | null;
    fik_number: string | null;
    qr_code_url: string | null;
    website_url: string | null;
    active: boolean;
    blocks: {
        block_key: string;
        content: string | null;
        y_position: number;
        font_size: number;
        max_width: number;
        max_lines: number | null;
        alignment: string;
        is_system_block: boolean;
    }[];
}): Promise<CreatePrintTemplateResponse> => {
    const res = await apiBase.post(`/v3/fines/print-templates`, data);

    if (res.status > 299) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: { id: 0 },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as CreatePrintTemplateResponse;
};

const updatePrintTemplate = async (
    id: number,
    data: {
        template_name: string;
        logo_url: string | null;
        company_name: string | null;
        legal_entity: string | null;
        cvr_number: string | null;
        fik_number: string | null;
        qr_code_url: string | null;
        website_url: string | null;
        active: boolean;
        blocks: {
            block_key: string;
            content: string | null;
            y_position: number;
            font_size: number;
            max_width: number;
            max_lines: number | null;
            alignment: string;
            is_system_block: boolean;
        }[];
    }
): Promise<ApiResponse> => {
    const res = await apiBase.put(`/v3/fines/print-templates/${id}`, data);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        } as ApiResponse;
    }

    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const deletePrintTemplate = async (id: number): Promise<ApiResponse> => {
    const res = await apiBase.delete(`/v3/fines/print-templates/${id}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        } as ApiResponse;
    }

    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const getTemplateVariables = async (): Promise<TemplateVariablesResponse> => {
    const res = await apiBase.get(`/v3/fines/print-templates/variables`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: { variables: [] },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as TemplateVariablesResponse;
};

const getListOfFineInterruptions = async (
    params: GetListOfFinesInterruptionReq
): Promise<GetListOfFinesInterruptionResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('limit', params.limit.toString());
    queryParams.append('offset', params.offset.toString());
    queryParams.append('search_term', params.search_term ?? '');
    queryParams.append('sort_order', params.sortorder ?? '');
    queryParams.append('sort_col', params.sortcol ?? '');
    queryParams.append('parkinglot_ids', params.parkinglot_ids ?? '');
    queryParams.append('user_ids', params.user_ids ?? '');
    queryParams.append('from', params.from ?? '');
    queryParams.append('to', params.to ?? '');
    queryParams.append('time_from', params.time_from ?? '');
    queryParams.append('time_to', params.time_to ?? '');
    queryParams.append('event_comment_ids', params.event_comment_ids ?? '');

    const res = await apiBase.get(
        `/v3/fines/interruption?${queryParams.toString()}`
    );
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                count: 0,
                data: [],
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            data: res.data.data,
            count: res.data.count,
        },
    } as GetListOfFinesInterruptionResponse;
};

const getListOfExternalNotes = async (
    params: GetListOfExternalNotesReq
): Promise<GetListOfExternalNotesResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('limit', params.limit.toString());
    queryParams.append('offset', params.offset.toString());
    queryParams.append('search_term', params.search_term ?? '');
    queryParams.append('sort_order', params.sortorder ?? '');
    queryParams.append('sort_col', params.sortcol ?? '');

    const res = await apiBase.get(
        `/v3/fines/external-notes?${queryParams.toString()}`
    );
    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: {
                count: 0,
                data: [],
            },
        };
    }

    return {
        error: null,
        statusCode: res.status,
        payload: {
            data: res.data.data,
            count: res.data.count,
        },
    } as GetListOfExternalNotesResponse;
};

const createExternalNoteTemplate = async (
    notes: ExternalNote
): Promise<ApiResponse> => {
    const res = await apiBase.post(`/v3/fines/external-notes`, {
        name: notes.name,
        description: notes.description,
        key_values: JSON.stringify(notes.key_values),
    });
    if (res.status >= 400) {
        return {
            error: `${res.data.error || ''}: ${res.data?.details || ''}`,
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as CreateExternalNoteResponse;
};

const updateExternalNoteTemplate = async (
    notes: ExternalNote
): Promise<ApiResponse> => {
    const res = await apiBase.put(`/v3/fines/external-notes/${notes.id}`, {
        id: notes.id,
        name: notes.name,
        description: notes.description,
        key_values: JSON.stringify(notes.key_values),
    });

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        } as ApiResponse;
    }

    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const deleteExternalNoteTemplate = async (id: number): Promise<ApiResponse> => {
    const res = await apiBase.delete(`/v3/fines/external-notes/${id}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        } as ApiResponse;
    }
    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

const uploadFineImages = async (
    fineId: number,
    files: File[]
): Promise<ApiResponse> => {
    const data = new FormData();
    files.forEach((file) => {
        data.append('file', file);
        data.append(
            'timestamp',
            formatWithPattern(file.lastModified, 'YYYY-MM-DD HH mm ss')
        );
    });

    const res = await apiBase.post(`/v3/fines/${fineId}/images`, data);

    if (res.status !== 200) {
        return { error: 'bad request', statusCode: res.status } as ApiResponse;
    }
    return { error: null, statusCode: res.status } as ApiResponse;
};

const deleteFineImage = async (
    fineId: number,
    imageId: number
): Promise<ApiResponse> => {
    const res = await apiBase.delete(`/v3/fines/${fineId}/images/${imageId}`);

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
        } as ApiResponse;
    }
    return {
        error: null,
        statusCode: res.status,
    } as ApiResponse;
};

export const backofficeFineAPI = {
    updateFine,
    updateFines,
    getFine,
    createFine,
    saveCommentForFine,
    getListOfFines,
    getFinesWithFilterParams,
    generateFineReport,
    saveCommentForMultipleFines,
    getFineStatusOptions,
    getCarBrands,
    sendPaidFineMail,
    cancelFine,
    getFineTableData,
    getFinesCount,
    getOffense,
    getOffenseDetails,
    updateOffenseDetails,
    createOffenseDetails,
    deleteOffenseDetails,
    getAvailableWrongDoings,
    getFineClearReasons,
    createFineClearDetails,
    updateClearReasonsDetails,
    deleteClearDetails,
    getCancelReasonDetails,
    createCancelReasonDetails,
    updateCancelReasonDetails,
    deleteCancelReasonDetails,
    generateEventLogReport,
    getPrintTemplates,
    getPrintTemplateDetail,
    createPrintTemplate,
    updatePrintTemplate,
    deletePrintTemplate,
    getTemplateVariables,
    getListOfFineInterruptions,
    getListOfExternalNotes,
    createExternalNoteTemplate,
    updateExternalNoteTemplate,
    deleteExternalNoteTemplate,
    deleteFineImage,
    uploadFineImages,
};
